import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader } from "@/components/PageHeader";
import { CheckCircle2, Clock } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { StatusPill } from "@/pages/WorkshopCalendar";
import { Link } from "react-router-dom";

const CATEGORY_LABEL = {
  maintenance: "Mantención",
  repair: "Reparación",
  service: "Servicio",
  others: "Otros",
};

export default function MyBookings() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("active");

  const load = () => api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const act = async (id, path, body) => {
    try {
      await api.post(`/bookings/${id}/${path}`, body || {});
      toast.success("Acción realizada");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const cancel = async (id) => {
    const reason = window.prompt("Motivo de cancelación (opcional):", "") || "";
    await act(id, "cancel", { reason });
  };

  const filtered = bookings.filter((b) =>
    tab === "active"
      ? ["requested", "accepted", "confirmed"].includes(b.status)
      : ["completed", "rejected", "cancelled"].includes(b.status),
  );

  const canConfirm = user.role === "rentacar_admin" || user.role === "superadmin";
  const canCancel = user.role !== "client_user";

  return (
    <div data-testid="my-bookings-page">
      <PageHeader
        tag="Reservas"
        title="Mis Reservas"
        description={
          user.role === "client_user"
            ? "Citas en taller confirmadas para tus vehículos arrendados."
            : "Solicitudes que has enviado o recibido."
        }
        action={
          user.role === "rentacar_admin" && (
            <Link
              to="/agendar"
              className="h-touch px-6 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 inline-flex items-center gap-2"
              data-testid="goto-create-booking"
            >
              + Nueva reserva
            </Link>
          )
        }
      />

      <div className="flex gap-2 mb-4 border-b border-gray-200">
        <TabBtn active={tab === "active"} onClick={() => setTab("active")} testId="tab-active">
          Activas
        </TabBtn>
        <TabBtn active={tab === "history"} onClick={() => setTab("history")} testId="tab-history">
          Historial
        </TabBtn>
      </div>

      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <Th>Patente</Th>
              <Th>Taller</Th>
              <Th>Inicio</Th>
              <Th>Categoría</Th>
              <Th>Tipo</Th>
              <Th>Estado</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <tr key={b.id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`my-booking-row-${b.id}`}>
                <td className="px-4 py-4 font-mono font-bold">{b.vehicle?.plate || b.vehicle_plate}</td>
                <td className="px-4 py-4 text-sm">{b.workshop?.name || "-"}</td>
                <td className="px-4 py-4 text-sm">{fmtDateTime(b.proposed_start)}</td>
                <td className="px-4 py-4 text-sm">{CATEGORY_LABEL[b.category] || b.category}</td>
                <td className="px-4 py-4 text-xs font-bold uppercase">
                  {b.is_overflow ? (
                    <span className="text-orange-600">Sobre cupo</span>
                  ) : (
                    "Cupo"
                  )}
                </td>
                <td className="px-4 py-4">
                  <StatusPill status={b.status} />
                </td>
                <td className="px-4 py-4 text-right space-x-2 whitespace-nowrap">
                  {canConfirm && b.status === "accepted" && (
                    <button
                      onClick={() => act(b.id, "confirm")}
                      className="inline-flex items-center gap-1 h-9 px-3 bg-black text-white text-xs font-bold uppercase hover:bg-blue-600"
                      data-testid={`confirm-${b.id}`}
                    >
                      <CheckCircle2 size={14} strokeWidth={2.5} /> Confirmar
                    </button>
                  )}
                  {canCancel && ["requested", "accepted", "confirmed"].includes(b.status) && (
                    <button
                      onClick={() => cancel(b.id)}
                      className="inline-flex items-center gap-1 h-9 px-3 border border-red-400 text-red-700 text-xs font-bold uppercase hover:bg-red-50"
                      data-testid={`my-cancel-${b.id}`}
                    >
                      Cancelar
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">
                  <Clock className="inline mr-2" size={14} strokeWidth={2.5} /> Sin reservas en esta vista.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">{children}</th>;
}
function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-4 ${
        active ? "border-black text-black" : "border-transparent text-gray-500 hover:text-black"
      }`}
    >
      {children}
    </button>
  );
}
